import { useEffect, useState } from 'react';
import { callEndpoint, telemetry } from '../../../../services/backend';
import {
  formatNearbyWeatherStationOptions,
  getReferenceLocationName,
  type NearbyWeatherStationOption,
  type NearbyWeatherStationQueryType,
  type NearbyWeatherStationsApiResponse,
} from '../helpers/location/nearbyWeatherStations';

const hasValidCoordinates = (
  latitude: number | undefined,
  longitude: number | undefined,
) =>
  latitude != null &&
  longitude != null &&
  Number.isFinite(latitude) &&
  Number.isFinite(longitude);

export const useNearbyWeatherStations = (
  latitude: number | undefined,
  longitude: number | undefined,
  type: NearbyWeatherStationQueryType,
  enabled: boolean,
) => {
  const [stations, setStations] = useState<NearbyWeatherStationOption[]>([]);
  const [referenceLocationName, setReferenceLocationName] = useState<
    string | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (!enabled || !hasValidCoordinates(latitude, longitude)) {
      setStations([]);
      setReferenceLocationName(null);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const loadNearbyStations = async () => {
      setIsLoading(true);
      setStations([]);
      setReferenceLocationName(null);

      try {
        const response = (await callEndpoint(
          callEndpoint.endpoints.queryNearbyWeatherStations,
          {},
          {
            latitude: latitude as number,
            longitude: longitude as number,
            type,
          },
        )) as NearbyWeatherStationsApiResponse;

        if (!isMounted) {
          return;
        }

        setReferenceLocationName(
          getReferenceLocationName(response?.referenceWeatherStation),
        );
        setStations(
          formatNearbyWeatherStationOptions(
            response?.nearbyWeatherStations,
            response?.referenceWeatherStation,
          ),
        );
      } catch (loadError) {
        telemetry(
          telemetry.WARN,
          `[useNearbyWeatherStations] ${JSON.stringify(loadError)}`,
        );

        if (isMounted) {
          setStations([]);
          setReferenceLocationName(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadNearbyStations();

    return () => {
      isMounted = false;
    };
  }, [enabled, latitude, longitude, type]);

  return {
    stations,
    referenceLocationName,
    isLoading,
  };
};
